import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Card, Field, PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { leadTitle, shownEmail } from '@/utils/format';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';
import { useSort, type SortColumns } from '@/utils/use-sort';

import type { DeliveryResult, MessageDetail, MessageInput, Recipient } from './api-types';
import { CHANNELS } from './channels';
import MessagingNav from './messaging-nav';
import { ChannelIcon, ChannelList, MessageStatusBadge, NotFound, PreviewPanel, ResultBadge, SegmentList, linkButtonLabel, messageDate } from './parts';

/** Per channel: sent (and how many of those only simulated), waiting, failed, and the Owner Hub notifications read. On a phone, one line per channel. */
const DeliveryTable = ({ m }: { m: MessageDetail }) => (
  <div>
    <ul className="divide-y divide-line md:hidden">
      {CHANNELS.filter((c) => m.channels.includes(c.id)).map(({ id, label }) => {
        const s = m.stats[id];
        return (
          <li key={id} className="py-2.5 first:pt-0">
            <p className="inline-flex items-center gap-1.5 text-sm text-fg">
              <ChannelIcon channel={id} />
              {label}
            </p>
            <p className="mt-0.5 text-xs tabular-nums text-fg-muted">
              {s?.sent ?? 0} sent, {s?.simulated ?? 0} of them simulated · {s?.waiting ?? 0} waiting ·{' '}
              <span className={s?.failed ? 'text-danger' : ''}>{s?.failed ?? 0} failed</span>
              {id === 'popup' && ` · ${s?.read ?? 0} read`}
            </p>
          </li>
        );
      })}
    </ul>
    <div className="hidden overflow-x-auto md:block">
      <table className={classNames(table.table, 'min-w-[520px]')}>
        <thead className={table.head}>
          <tr>
            <th scope="col" className={table.th}>Channel</th>
            <th scope="col" className={classNames(table.th, 'text-right')}>Sent</th>
            <th scope="col" className={classNames(table.th, 'text-right')}>Of those, simulated</th>
            <th scope="col" className={classNames(table.th, 'text-right')}>Waiting</th>
            <th scope="col" className={classNames(table.th, 'text-right')}>Failed</th>
            <th scope="col" className={classNames(table.th, 'text-right')}>Read</th>
          </tr>
        </thead>
        <tbody>
          {CHANNELS.filter((c) => m.channels.includes(c.id)).map(({ id, label }) => {
            const s = m.stats[id];
            return (
              <tr key={id} className={table.row}>
                <td className={table.td}>
                  <span className="inline-flex items-center gap-1.5 text-fg">
                    <ChannelIcon channel={id} />
                    {label}
                  </span>
                </td>
                <td className={classNames(table.td, 'text-right tabular-nums')}>{s?.sent ?? 0}</td>
                <td className={classNames(table.td, 'text-right tabular-nums text-fg-muted')}>{s?.simulated ?? 0}</td>
                <td className={classNames(table.td, 'text-right tabular-nums text-fg-muted')}>{s?.waiting ?? 0}</td>
                <td className={classNames(table.td, 'text-right tabular-nums', s?.failed ? 'text-danger' : '')}>{s?.failed ?? 0}</td>
                <td className={classNames(table.td, 'text-right tabular-nums text-fg-muted')} title={id === 'popup' ? `${s?.shown ?? 0} seen` : undefined}>
                  {id === 'popup' ? (s?.read ?? 0) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    <p className="mt-3 text-xs text-fg-muted">
      Prisma cannot tell yet whether an email was opened or a Telegram message read; for an Owner Hub notification it shows when the owner saw
      and read it. “Simulated” means nothing really left: no email provider is chosen yet, or the bot runs in simulate mode.
    </p>
  </div>
);

// A channel column sorts the failures first, then what still waits, then what went.
const RESULT_RANK: Record<DeliveryResult['status'], number> = { failed: 2, waiting: 1, sent: 0 };

/** Who the message went to, with the result on each channel. On a phone, one card per person. */
const Recipients = ({ m }: { m: MessageDetail }) => {
  const columns = useMemo(() => {
    const cols: SortColumns<Recipient> = { lead: { value: (r) => leadTitle(r.name, r.email) } };
    for (const c of m.channels) {
      cols[c] = { value: (r) => (r.results[c] ? RESULT_RANK[r.results[c].status] : undefined), desc: true };
    }
    return cols;
  }, [m.channels]);
  const { rows, sort, toggle } = useSort(m.recipients, columns);
  return (
    <div>
      <ul className="divide-y divide-line md:hidden">
        {rows.map((r) => (
          <li key={r.lead_id || r.email} className="py-3 first:pt-0">
            {r.lead_id ? (
              <Link to={`/leads/${r.lead_id}/messages`} className="block break-words py-3 text-sm text-fg-strong hover:text-brand md:py-0">
                {leadTitle(r.name, r.email)}
              </Link>
            ) : (
              <span className="block break-words text-sm text-fg-strong">{leadTitle(r.name, r.email)}</span>
            )}
            {r.name && shownEmail(r.email) && <p className="break-all text-xs text-fg-muted">{r.email}</p>}
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1.5">
              {m.channels.map((c) => {
                const res = r.results[c];
                return (
                  <span key={c} className="inline-flex items-center gap-1.5 text-xs text-fg-muted">
                    <ChannelIcon channel={c} />
                    {res ? <ResultBadge result={res} /> : <span className="text-fg-faint">{c === 'telegram' ? 'Not in a group' : '—'}</span>}
                  </span>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden overflow-x-auto md:block">
        <table className={classNames(table.table, 'min-w-[620px]')}>
          <thead className={table.head}>
            <tr>
              <SortTh label="Lead" column="lead" sort={sort} onSort={toggle} />
              {m.channels.map((c) => (
                <SortTh key={c} label={CHANNELS.find((x) => x.id === c)?.label} column={c} sort={sort} onSort={toggle} />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.lead_id || r.email} className={table.row}>
                <td className={table.td}>
                  {r.lead_id ? (
                    <Link to={`/leads/${r.lead_id}/messages`} className="text-fg-strong hover:text-brand">
                      {leadTitle(r.name, r.email)}
                    </Link>
                  ) : (
                    <span className="text-fg-strong">{leadTitle(r.name, r.email)}</span>
                  )}
                  {r.name && shownEmail(r.email) && <p className="text-xs text-fg-muted">{r.email}</p>}
                </td>
                {m.channels.map((c) => {
                  const res = r.results[c];
                  return (
                    <td key={c} className={table.td}>
                      {res ? <ResultBadge result={res} /> : <span className="text-xs text-fg-faint">{c === 'telegram' ? 'Not in a group' : '—'}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {m.recipients.length < m.audience && <p className="mt-3 text-xs text-fg-muted">The first {m.recipients.length} of {m.audience} people.</p>}
    </div>
  );
};

/** One message: what it said, who it was for, and how each channel delivered it (OUT-1…4). */
const MessagePage = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: m, error, reload } = useApi<MessageDetail>(`/messages/${encodeURIComponent(id)}`);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (error === 'Not found.') return <NotFound what="message" back="/messaging/messages" label="Back to messages" />;
  if (error && !m) return <StateMessage title="The message could not be loaded" tone="error">{error}</StateMessage>;
  if (!m) return <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />;

  const run = async (call: () => Promise<unknown>, after: () => void) => {
    setBusy(true);
    setFailed(false);
    try {
      await call();
      after();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const duplicate: MessageInput = {
    title: `${m.title} (copy)`.slice(0, 120),
    body: m.body,
    channels: m.channels,
    segment_ids: m.segment_ids,
    template_id: m.template_id,
    link_url: m.link_url,
    link_label: m.link_label,
  };

  let actions;
  if (m.status === 'draft') {
    actions = (
      <>
        <button type="button" className={btn.danger} disabled={busy} onClick={() => run(() => http.delete(`/messages/${m.id}`), () => navigate('/messaging/messages'))}>
          Delete
        </button>
        <Link to={`/messaging/messages/${m.id}/edit`} className={btn.secondary}>
          Edit
        </Link>
        <Link to={`/messaging/messages/${m.id}/review`} className={btn.primary}>
          Review &amp; send
        </Link>
      </>
    );
  } else if (m.status === 'scheduled') {
    actions = (
      <>
        <button type="button" className={btn.secondary} disabled={busy} onClick={() => run(() => http.post(`/messages/${m.id}/unschedule`), reload)}>
          Cancel schedule
        </button>
        <Link to={`/messaging/messages/${m.id}/review`} className={btn.secondary}>
          Change the time
        </Link>
      </>
    );
  } else if (m.status !== 'sending') {
    actions = (
      <button type="button" className={btn.secondary} onClick={() => navigate('/messaging/messages/new', { state: duplicate })}>
        Duplicate
      </button>
    );
  }

  return (
    <div>
      <PageHeader title={<span className="break-words">{m.title}</span>} back={{ to: '/messaging/messages', label: 'Messages' }} actions={actions}>
        <MessagingNav />
      </PageHeader>
      {failed && <p className="mb-4 text-sm text-danger">That did not work. Try again.</p>}
      {m.status === 'sending' && (
        <p role="status" className="mb-4 text-sm text-fg-muted">
          Going out now.{' '}
          <button type="button" className={btn.link} onClick={reload}>
            Refresh
          </button>
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-4 md:space-y-6">
          <Card title="Message" aside={m.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}>
            <dl>
              <Field label="Status">
                <MessageStatusBadge status={m.status} />
              </Field>
              <Field label={m.status === 'sent' ? 'Sent' : m.status === 'scheduled' ? 'Scheduled' : 'Date'}>{messageDate(m)}</Field>
              {m.error && <Field label="Why it failed">{m.error}</Field>}
              <Field label="Channels">
                <ChannelList channels={m.channels} />
              </Field>
              <Field label="Segments">
                <SegmentList ids={m.segment_ids} max={6} empty={m.lead_id ? 'One lead, chosen by the trigger' : '—'} />
              </Field>
              {m.status === 'sent' && <Field label="Audience">{m.audience === 1 ? '1 person' : `${m.audience} people`}</Field>}
              {m.trigger_id ? (
                <Field label="Trigger">
                  <Link to={`/messaging/triggers/${m.trigger_id}`} className={btn.link}>
                    {m.trigger_name || 'A deleted trigger'}
                  </Link>
                </Field>
              ) : (
                <Field label="Written by">{m.created_by || '—'}</Field>
              )}
            </dl>
          </Card>
          <Card title="Text">
            <p className="whitespace-pre-wrap break-words rounded-lg bg-surface-inset p-4 text-sm text-fg">{m.body || <span className="text-fg-faint">No text yet.</span>}</p>
          </Card>
        </div>
        <Card title="Preview" aside={<span className="text-xs text-fg-faint">With sample values</span>}>
          <PreviewPanel channels={m.channels} title={m.title} body={m.body} linkLabel={linkButtonLabel(m)} />
        </Card>
      </div>

      {m.status === 'sent' ? (
        <div className="mt-4 space-y-4 md:mt-6 md:space-y-6">
          <Card title="Delivery by channel">
            <DeliveryTable m={m} />
          </Card>
          <Card title="Recipients">
            {m.recipients.length ? <Recipients m={m} /> : <p className="text-sm text-fg-faint">It reached nobody: no one in its segments could get it on these channels.</p>}
          </Card>
        </div>
      ) : (
        <Card title="Delivery" className="mt-4 md:mt-6">
          <p className="text-sm text-fg-faint">{m.status === 'failed' ? 'Nothing went out.' : 'Shows here once the message is sent.'}</p>
        </Card>
      )}
    </div>
  );
};

export default MessagePage;
